import { User } from '@/api';
import useAuth from '@/contexts/AuthContext';
import { ErrorHandler } from '@/helpers/ErrorHandler';
import { ConflictException } from '@/helpers/Exceptions';
import { userApi } from '@/queries/auth';
import { identityApi } from '@/queries/identity';
import { useStripe } from '@stripe/react-stripe-js';
import i18next from 'i18n';
import { useCallback, useState } from 'react';
import { toast } from 'react-hot-toast';

/** Statuses from which a customer can (re)start Stripe Identity. */
export const canStartIdentityVerification = (status: User.KycVerificationStatusEnum | null | undefined) =>
    status == null || status === User.KycVerificationStatusEnum.RequiresInput || status === User.KycVerificationStatusEnum.Canceled;

/**
 * Opens Stripe Identity and stores the outcome on the user. Shared by the
 * settings button and the credit request, which starts verification right
 * after an unverified customer submits.
 *
 * Needs a Stripe `<Elements>` ancestor; `ready` stays false until Stripe.js
 * has loaded.
 */
const useStartIdentityVerification = () => {
    const { setUser } = useAuth();
    const stripe = useStripe();
    const [loading, setLoading] = useState(false);

    const start = useCallback(async () => {
        if (!stripe || loading) return;
        setLoading(true);

        try {
            const redirectUrl = `${window.location.href}`;
            const { secret } = await identityApi.identityControllerStartVerification({ redirect_url: redirectUrl });

            // Show the verification modal.
            const { verificationSession, error } = await stripe.verifyIdentity(secret);

            if (error) {
                if (error.message) { toast.error(error.message || ''); }
            } else if (verificationSession) {
                const updatedUser = await identityApi.identityControllerSubmitVerification(verificationSession.id);
                if (updatedUser.kyc_verification_status === User.KycVerificationStatusEnum.Verified) {
                    toast.success(i18next.t('success-notification.identity-verified'));
                } else {
                    toast.success(i18next.t('success-notification.identity-submitted'));
                }
                setUser(updatedUser);
            } else {
                toast.success(i18next.t('success-notification.identity-submitted'));
                setUser(await userApi.userControllerMe());
            }
        } catch (error) {
            const exception = await ErrorHandler.transformError(error as Error);
            if (exception instanceof ConflictException) {
                toast.error(exception.message);
            }
        }
        setLoading(false);
    }, [loading, setUser, stripe]);

    return { start, loading, ready: !!stripe };
};

export default useStartIdentityVerification;
